import { RequestHandler, IRequestHandler } from '../lib/RequestHandler'
import { store } from '../store'
import { invoices } from '../store/invoices/slice'
import { Receipt, TaxInformation } from '../ts/interfaces/invoices'
import { BulkScheduleInvoicesBody, BulkScheduleInvoicesResponse, CreateTaxInformationBody, GetInvoicesStatusResponse, GetTaxInformationListResponse, GetTaxInformationByIdResponse, UpdateTaxInformationBody, UpdateTaxInformationResponse, CreateTaxInformationResponse, GetAllReceiptsResponse, GetReceiptResponse, DeleteTaxInformationResponse, EnableTaxInformationResponse, GetInvoicesActiveResponse } from '../ts/interfaces/request'

export interface ExtractTaxCertificateResponse {
  first_name: string | null
  middle_name: string | null
  surname: string | null
  last_name: string | null
  business_name: string | null
  tax_id: string | null
  tax_regime: number | null
  postal_code: string | null
  email: string | null
  street: string | null
  external_number: string | null
  internal_number: string | null
  neighborhood: string | null
  district: string | null
  city: string | null
  state: string | null
  country: string | null
  regime_count: number
}

export const InvoicesService = (props?: IRequestHandler) => ({
  requestHandler: RequestHandler(props),

  async getInvoicesStatus(props: { fetch_stats: boolean; signal?: AbortSignal; cache?: boolean }) {
    try {
      const params: Record<string, any> = {
        fetch_stats: props.fetch_stats ? 'True' : 'False',
        cache: props.cache || true
      }
      const urlSearchParams = new URLSearchParams(params)
      const response = await this.requestHandler.get<GetInvoicesStatusResponse>(`invoices/status?${urlSearchParams}`, {
        signal: props.signal
      })
      if (response?.data?.data) {
        store.dispatch(invoices.actions.setInvoicesState(response.data.data))
        return response.data.data
      }
    } catch (error) {
      throw error
    }
  },
  async getInvoicesActive() {
    try {
      const response = await this.requestHandler.get<GetInvoicesActiveResponse>('invoices/active')
      if (response?.data?.data) {
        store.dispatch(invoices.actions.setInvoicesState(response.data.data))
        return response.data.data
      }
    } catch (error) {
      throw error
    }
  },
  async getAllTaxInformation() {
    try {
      const urlSearchParams = new URLSearchParams({
        retrieve_all: 'True'
      })
      const response = await this.requestHandler.get<GetTaxInformationListResponse>(`invoices/tax-information?${urlSearchParams}`)
      if (response?.data?.data) {
        store.dispatch(invoices.actions.setTaxInformationList(response.data.data))
        return response.data.data
      }
      return []
    } catch (error) {
      throw error
    }
  },
  async getUserTaxInformation(setDataInState?: boolean): Promise<GetTaxInformationListResponse['data']> {
    try {
      const response = await this.requestHandler.get<GetTaxInformationListResponse>(`invoices/users/tax-information`)
      if (response?.data?.data) {
        if (setDataInState) store.dispatch(invoices.actions.setTaxInformationList(response.data.data))
        return response.data.data
      }
      return []
    } catch (error) {
      throw error
    }
  },
  async getTaxInformationById(id: TaxInformation['id']): Promise<TaxInformation | null> {
    try {
      const response = await this.requestHandler.get<GetTaxInformationByIdResponse>(`invoices/tax-information/${id}`)
      if (response?.data?.data) return response.data.data
      return null
    } catch (error) {
      throw error
    }
  },
  async getAllReceipts() {
    try {
      const response = await this.requestHandler.get<GetAllReceiptsResponse>('invoices/receipts')
      if (response?.data?.data) store.dispatch(invoices.actions.setReceiptsList(response.data.data))
    } catch (error) {
      throw error
    }
  },
  async getReceipt(id: Receipt['id']) {
    try {
      const response = await this.requestHandler.get<GetReceiptResponse>(`invoices/receipt/${id}`)
      return response.data
    } catch (error) {
      throw error
    }
  },
  async extractTaxCertificate(tax_certificate: string): Promise<ExtractTaxCertificateResponse | null> {
    try {
      const response = await this.requestHandler.post<ExtractTaxCertificateResponse>('invoices/tax-information/extract-certificate', { tax_certificate }, { timeout: 120_000 })
      return response?.data ?? null
    } catch (error) {
      throw error
    }
  },

  async createTaxInformation(data: CreateTaxInformationBody) {
    return await this.requestHandler.post<CreateTaxInformationResponse>('invoices/tax-information', data)
  },
  async updateTaxInformation(data: UpdateTaxInformationBody) {
    return await this.requestHandler.patch<UpdateTaxInformationResponse>('invoices/tax-information', data)
  },
  async deleteTaxInformation(taxInformationId: TaxInformation['id']) {
    return await this.requestHandler.delete<DeleteTaxInformationResponse>(`invoices/tax-information/${taxInformationId}`)
  },
  async enableTaxInformation(taxInformationId: TaxInformation['id']) {
    return await this.requestHandler.post<EnableTaxInformationResponse>(`invoices/tax-information/enable/${taxInformationId}`)
  },
  async scheduleInvoicesBulk(data: BulkScheduleInvoicesBody) {
    return await this.requestHandler.post<BulkScheduleInvoicesResponse>('invoices/schedule/bulk', data)
  },

  async getBulkUploadUrls(files: { file_name: string; content_type: string }[]) {
    return await this.requestHandler.post<{
      urls: { signed_url: string; gcs_path: string; index: number }[]
    }>('invoices/bulk/upload-urls', { files })
  },

  async scheduleBulkFromGcs(data: { tax_information_id: number; cfdi: string; payment_method: string; tickets: { gcs_path: string; file_name: string; content_type: string }[]; tag_ids?: number[] }) {
    return await this.requestHandler.post<{ job_id: number; total_tickets: number }>('invoices/bulk/schedule', data)
  },

  async getBulkJobStatus(jobId: number) {
    return await this.requestHandler.get<{
      id: number
      status: string
      total_tickets: number
      completed_tickets: number
      failed_tickets: number
      tickets: {
        index: number
        file_name: string | null
        status: string
        error_message: string | null
        expense_id: number | null
      }[]
      created_at: string
    }>(`invoices/bulk/jobs/${jobId}`)
  }
})
